import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  TextField, Button, MenuItem, Divider, Tooltip, Stack, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, FormGroup
} from '@mui/material';
import {
  ArrowBack, Home, Schedule, Add, Delete, Save, RestartAlt, AutoFixHigh, ContentCopy
} from '@mui/icons-material';
import axios from 'axios';
import TimetableNav from './TimetableNav';

const API = process.env.REACT_APP_API_URL;

const PERIOD_TYPES = ['lesson', 'break', 'lunch', 'games', 'assembly'];
const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TYPE_COLOR = {
  lesson: '#1565c0', break: '#f57c00', lunch: '#6a1b9a', games: '#2e7d32', assembly: '#00838f',
};

const TimetableSetup = () => {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [years, setYears]   = useState([]);
  const [year, setYear]     = useState('');
  const [term, setTerm]     = useState(1);
  const [stages, setStages] = useState([]);

  const [config, setConfig] = useState(null);
  const [days, setDays]     = useState([]);
  const [bands, setBands]   = useState([]);   // [{ bandCode, name, appliesToStages, periods }]
  const [fixed, setFixed]   = useState([]);   // [{ label, day, periodNo }] whole-school reserved slots

  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyForm, setCopyForm] = useState({ fromYear: '', fromTerm: 1, config: true, allocations: true, entries: false });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/academics/years`, { headers }),
      axios.get(`${API}/api/stages`, { headers }),
      axios.get(`${API}/api/academics/current-period`, { headers }).catch(() => ({ data: {} })),
    ]).then(([y, s, cp]) => {
      setYears(y.data);
      const active = y.data.find(yr => yr.isActive) || y.data[0];
      if (cp.data?.year) setYear(cp.data.year);
      else if (active)   setYear(active.year);
      if (cp.data?.term) setTerm(cp.data.term);
      setStages(s.data || []);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load reference data'));
  }, []); // eslint-disable-line

  // Migrate a legacy single-schedule config into one editable band.
  const bandsFromConfig = (data) => {
    if (data.bands && data.bands.length) return data.bands;
    if (data.periods && data.periods.length) {
      return [{ bandCode: 'DEFAULT', name: 'Default', appliesToStages: stages.map(s => s.code), periods: data.periods }];
    }
    return [];
  };

  const loadConfig = useCallback(async () => {
    if (!year || !term) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.get(`${API}/api/timetable/config`, { headers, params: { academicYear: year, term } });
      if (data) {
        setConfig(data);
        setDays(data.daysOfWeek || []);
        setBands(bandsFromConfig(data));
        setFixed(data.fixedActivities || []);
      } else {
        setConfig(null); setDays([]); setBands([]); setFixed([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load timetable setup');
    } finally { setLoading(false); }
  }, [year, term, stages]); // eslint-disable-line

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const seed = async (reset = false) => {
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/timetable/config/seed`, { academicYear: year, term, reset }, { headers });
      setConfig(data); setDays(data.daysOfWeek); setBands(bandsFromConfig(data)); setFixed(data.fixedActivities || []);
      setSuccess(reset ? 'Reset to the default 60-min / 40-min bands' : 'Seeded the default bands');
    } catch (err) {
      setError(err.response?.data?.error || 'Seeding failed');
    } finally { setSaving(false); }
  };

  const save = async () => {
    // A stage may only sit in one band.
    const seen = new Set();
    for (const b of bands) for (const st of (b.appliesToStages || [])) {
      if (seen.has(st)) { setError(`${stageName(st)} is in more than one band — each stage belongs to one band.`); return; }
      seen.add(st);
    }
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/timetable/config`,
        { academicYear: year, term, daysOfWeek: days, bands, fixedActivities: fixed }, { headers });
      setConfig(data); setBands(bandsFromConfig(data)); setFixed(data.fixedActivities || []);
      setSuccess('Timetable setup saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const openCopy = () => {
    setCopyForm(f => ({ ...f, fromYear: f.fromYear || year, fromTerm: term === 1 ? 3 : term - 1 }));
    setCopyOpen(true);
  };
  const runCopy = async () => {
    setCopyBusy(true); setError('');
    try {
      const include = ['config', 'allocations', 'entries'].filter(k => copyForm[k]);
      const { data } = await axios.post(`${API}/api/timetable/copy`, {
        fromYear: copyForm.fromYear, fromTerm: copyForm.fromTerm,
        toYear: year, toTerm: term, include,
      }, { headers });
      setCopyOpen(false);
      setSuccess(data.summary || 'Copied');
      loadConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Copy failed');
    } finally { setCopyBusy(false); }
  };

  const stageName = (code) => stages.find(s => s.code === code)?.name || code;

  const toggleDay = (d) =>
    setDays(ds => ds.includes(d) ? ds.filter(x => x !== d) : [...ALL_DAYS].filter(x => ds.includes(x) || x === d));

  // ── Band editing ──
  const setBand = (bi, patch) => setBands(bs => bs.map((b, i) => i === bi ? { ...b, ...patch } : b));
  const addBand = () => setBands(bs => [...bs, {
    bandCode: `BAND${bs.length + 1}`, name: `Band ${bs.length + 1}`, appliesToStages: [], periods: [],
  }]);
  const removeBand = (bi) => setBands(bs => bs.filter((_, i) => i !== bi));

  const updatePeriod = (bi, pi, field, value) =>
    setBands(bs => bs.map((b, i) => i !== bi ? b
      : { ...b, periods: b.periods.map((p, j) => j === pi ? { ...p, [field]: value } : p) }));
  const addPeriod = (bi) => setBands(bs => bs.map((b, i) => {
    if (i !== bi) return b;
    const nextNo = b.periods.length ? Math.max(...b.periods.map(p => p.periodNo || 0)) + 1 : 1;
    return { ...b, periods: [...b.periods, { periodNo: nextNo, startTime: '', endTime: '', type: 'lesson', label: '' }] };
  }));
  const removePeriod = (bi, pi) =>
    setBands(bs => bs.map((b, i) => i !== bi ? b : { ...b, periods: b.periods.filter((_, j) => j !== pi) }));

  // ── Fixed activities (whole-school reserved slots: PPI, assembly…) ──
  const setFixedRow = (fi, patch) => setFixed(fs => fs.map((f, i) => i === fi ? { ...f, ...patch } : f));
  const addFixed = () => setFixed(fs => [...fs, { label: '', day: (days[0] || 'Fri'), periodNo: 1, bandCodes: [] }]);
  const removeFixed = (fi) => setFixed(fs => fs.filter((_, i) => i !== fi));
  const periodNoOptions = (() => {
    const set = new Set(bands.flatMap(b => (b.periods || []).filter(p => p.type === 'lesson').map(p => p.periodNo)));
    const arr = [...set].sort((a, b) => a - b);
    return arr.length ? arr : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  })();

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, px: { xs: 1, sm: 0 } }}>
      <TimetableNav />
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Schedule color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Timetable Setup</Typography>
                <Typography variant="body2" color="text.secondary">
                  Bell schedules (bands) — each with its own times; assign stages to a band
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics"><ArrowBack /></IconButton>
              <IconButton onClick={() => navigate('/dashboard')} title="Home"><Home /></IconButton>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <TextField select label="Academic Year" value={year} size="small" sx={{ minWidth: 180 }}
              onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={term} size="small" sx={{ minWidth: 120 }}
              onChange={e => setTerm(parseInt(e.target.value, 10))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <Tooltip title="Copy setup/allocations from another term into this one">
              <Button variant="outlined" startIcon={<ContentCopy />} onClick={openCopy} disabled={!year}>Copy from…</Button>
            </Tooltip>
          </Stack>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box textAlign="center" py={5}><CircularProgress /></Box>
          ) : !config && bands.length === 0 ? (
            <Box textAlign="center" py={5}>
              <Typography color="text.secondary" mb={2}>No timetable setup for {year || '—'} Term {term} yet.</Typography>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="contained" startIcon={<AutoFixHigh />} onClick={() => seed(false)} disabled={saving || !year}>
                  Seed default bands
                </Button>
                <Button variant="outlined" startIcon={<ContentCopy />} onClick={openCopy} disabled={!year}>Copy from another term</Button>
              </Stack>
              <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                Creates a Senior 60-min band and a Standard 40-min band, Mon–Fri — edit times & stages after.
              </Typography>
            </Box>
          ) : (
            <>
              {/* School days */}
              <Typography variant="subtitle2" fontWeight={700} mb={1}>School Days</Typography>
              <Box display="flex" gap={1} mb={3} flexWrap="wrap">
                {ALL_DAYS.map(d => (
                  <Chip key={d} label={d} clickable
                    color={days.includes(d) ? 'primary' : 'default'}
                    variant={days.includes(d) ? 'filled' : 'outlined'}
                    onClick={() => toggleDay(d)} />
                ))}
              </Box>

              {/* Bands */}
              {bands.map((band, bi) => {
                const lessonCount = (band.periods || []).filter(p => p.type === 'lesson').length;
                return (
                  <Card key={bi} variant="outlined" sx={{ mb: 2.5 }}>
                    <CardContent>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} mb={1.5}>
                        <TextField label="Band name" value={band.name || ''} size="small" sx={{ minWidth: 180 }}
                          onChange={e => setBand(bi, { name: e.target.value })} />
                        <TextField label="Code" value={band.bandCode || ''} size="small" sx={{ width: 120 }}
                          onChange={e => setBand(bi, { bandCode: e.target.value.toUpperCase() })} />
                        <Autocomplete multiple size="small" sx={{ flex: 1, minWidth: 220 }}
                          options={stages.map(s => s.code)}
                          getOptionLabel={(c) => stageName(c)}
                          value={band.appliesToStages || []}
                          onChange={(_, v) => setBand(bi, { appliesToStages: v })}
                          renderInput={(params) => <TextField {...params} label="Stages in this band" placeholder="pick stages" />}
                        />
                        <Chip size="small" label={`${lessonCount} lessons`} />
                        <Tooltip title="Remove band">
                          <IconButton color="error" onClick={() => removeBand(bi)}><Delete /></IconButton>
                        </Tooltip>
                      </Stack>

                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">Periods (real clock times)</Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => addPeriod(bi)}>Add row</Button>
                      </Box>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>#</strong></TableCell>
                              <TableCell><strong>Lesson</strong></TableCell>
                              <TableCell><strong>Start</strong></TableCell>
                              <TableCell><strong>End</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Label</strong></TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(band.periods || []).map((p, pi) => (
                              <TableRow key={pi} hover>
                                <TableCell sx={{ width: 60 }}>
                                  <TextField value={p.periodNo ?? ''} type="number" size="small" variant="standard" sx={{ width: 46 }}
                                    onChange={e => updatePeriod(bi, pi, 'periodNo', parseInt(e.target.value, 10))} />
                                </TableCell>
                                <TableCell sx={{ width: 70 }}>
                                  <TextField value={p.lessonNo ?? ''} type="number" size="small" variant="standard" sx={{ width: 46 }}
                                    disabled={p.type !== 'lesson'}
                                    onChange={e => updatePeriod(bi, pi, 'lessonNo', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} />
                                </TableCell>
                                <TableCell>
                                  <TextField value={p.startTime || ''} type="time" size="small" variant="standard"
                                    inputProps={{ step: 300 }} onChange={e => updatePeriod(bi, pi, 'startTime', e.target.value)} />
                                </TableCell>
                                <TableCell>
                                  <TextField value={p.endTime || ''} type="time" size="small" variant="standard"
                                    inputProps={{ step: 300 }} onChange={e => updatePeriod(bi, pi, 'endTime', e.target.value)} />
                                </TableCell>
                                <TableCell sx={{ width: 120 }}>
                                  <TextField select value={p.type || 'lesson'} size="small" variant="standard" fullWidth
                                    onChange={e => updatePeriod(bi, pi, 'type', e.target.value)}>
                                    {PERIOD_TYPES.map(t => (
                                      <MenuItem key={t} value={t}>
                                        <Chip size="small" label={t} sx={{ bgcolor: `${TYPE_COLOR[t]}22`, color: TYPE_COLOR[t], textTransform: 'capitalize' }} />
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>
                                <TableCell>
                                  <TextField value={p.label || ''} size="small" variant="standard" placeholder="—"
                                    onChange={e => updatePeriod(bi, pi, 'label', e.target.value)} />
                                </TableCell>
                                <TableCell sx={{ width: 40 }}>
                                  <IconButton size="small" color="error" onClick={() => removePeriod(bi, pi)}><Delete fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              <Button startIcon={<Add />} variant="text" onClick={addBand} sx={{ mb: 2 }}>Add band</Button>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={700}>Whole-school fixed activities</Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Reserve a period so no class gets a lesson then — e.g. <strong>PPI</strong> on Friday's first period, or an
                assembly. Leave <em>Bands</em> blank to apply to the whole school, or pick specific bands (e.g. PPI on the
                CBC band only). Auto-fill skips these slots, manual placement is blocked, and they print with their label.
              </Typography>
              {fixed.length === 0 && (
                <Typography variant="body2" color="text.secondary" mb={1}>None yet.</Typography>
              )}
              <Stack spacing={1} mb={1}>
                {fixed.map((f, fi) => (
                  <Stack key={fi} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField label="Label" size="small" value={f.label} placeholder="PPI"
                      onChange={e => setFixedRow(fi, { label: e.target.value })} sx={{ minWidth: 180 }} />
                    <TextField select label="Day" size="small" value={f.day}
                      onChange={e => setFixedRow(fi, { day: e.target.value })} sx={{ minWidth: 120 }}>
                      {(days.length ? days : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </TextField>
                    <TextField select label="Period" size="small" value={f.periodNo}
                      onChange={e => setFixedRow(fi, { periodNo: parseInt(e.target.value, 10) })} sx={{ minWidth: 110 }}>
                      {periodNoOptions.map(n => <MenuItem key={n} value={n}>P{n}</MenuItem>)}
                    </TextField>
                    <Autocomplete multiple size="small" options={bands.map(b => b.bandCode)}
                      getOptionLabel={(code) => { const b = bands.find(x => x.bandCode === code); return b ? (b.name || b.bandCode) : code; }}
                      value={f.bandCodes || []} onChange={(_, v) => setFixedRow(fi, { bandCodes: v })}
                      renderInput={(params) => <TextField {...params} label="Bands (blank = all)" />}
                      sx={{ minWidth: 230, flex: 1 }} />
                    <IconButton size="small" color="error" onClick={() => removeFixed(fi)}><Delete fontSize="small" /></IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button startIcon={<Add />} variant="text" onClick={addFixed} sx={{ mb: 2 }}>Add fixed activity</Button>

              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save setup'}
                </Button>
                <Tooltip title="Overwrite everything with the default 60-min & 40-min bands">
                  <Button variant="outlined" color="warning" startIcon={<RestartAlt />} onClick={() => seed(true)} disabled={saving}>
                    Reset to default
                  </Button>
                </Tooltip>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Copy dialog */}
      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Copy into {year} · Term {term}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Clone another term's timetable into this one. Allocations &amp; entries only copy when this term's are empty.
          </Typography>
          <Stack direction="row" spacing={2} mb={2}>
            <TextField select label="From Year" value={copyForm.fromYear} size="small" fullWidth
              onChange={e => setCopyForm(f => ({ ...f, fromYear: e.target.value }))}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
            <TextField select label="From Term" value={copyForm.fromTerm} size="small" sx={{ minWidth: 110 }}
              onChange={e => setCopyForm(f => ({ ...f, fromTerm: parseInt(e.target.value, 10) }))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
          </Stack>
          <FormGroup>
            <FormControlLabel control={<Checkbox checked={copyForm.config} onChange={e => setCopyForm(f => ({ ...f, config: e.target.checked }))} />} label="Bell schedules (setup)" />
            <FormControlLabel control={<Checkbox checked={copyForm.allocations} onChange={e => setCopyForm(f => ({ ...f, allocations: e.target.checked }))} />} label="Teacher allocations" />
            <FormControlLabel control={<Checkbox checked={copyForm.entries} onChange={e => setCopyForm(f => ({ ...f, entries: e.target.checked }))} />} label="Placed lessons (grid)" />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<ContentCopy />} onClick={runCopy}
            disabled={copyBusy || !copyForm.fromYear || (copyForm.fromYear === year && copyForm.fromTerm === term)}>
            {copyBusy ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableSetup;
